const deepEqual = (a, b) => {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return a === b;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let index = 0; index < a.length; index += 1) {
      if (!deepEqual(a[index], b[index])) return false;
    }
    return true;
  }

  const keysA = Object.keys(a).sort();
  const keysB = Object.keys(b).sort();
  if (keysA.length !== keysB.length) return false;
  for (let index = 0; index < keysA.length; index += 1) {
    if (keysA[index] !== keysB[index]) return false;
    if (!deepEqual(a[keysA[index]], b[keysB[index]])) return false;
  }
  return true;
};

export const reconcileDayStatusSnapshot = (
  existingDayStatuses,
  freshDayStatuses,
  oldestRefreshedDayKey = null,
) => {
  // The backend keys statuses by each returned activity's created-at day.
  // Therefore only keys strictly older than the refreshed source boundary
  // belong to the preserved activity tail. Keys on/after the boundary are
  // authoritative in the fresh snapshot and must disappear when omitted.
  const preservedTailStatuses = oldestRefreshedDayKey
    ? Object.fromEntries(
      Object.entries(existingDayStatuses || {})
        .filter(([dayKey]) => dayKey < oldestRefreshedDayKey),
    )
    : {};

  return { ...preservedTailStatuses, ...(freshDayStatuses || {}) };
};

export const reconcileActivitySnapshot = (existingActivities, freshActivities, preserveBeforeCreatedAt = null) => {
  const existingById = new Map(existingActivities.map(activity => [String(activity._id), activity]));
  // A late-completed activity can be unioned into the first response and then
  // encountered again when paging reaches its original created_at position.
  // Keep one canonical row per id before reconciling React keys.
  const freshById = new Map();
  freshActivities.forEach(activity => {
    const id = String(activity._id);
    if (!freshById.has(id)) freshById.set(id, activity);
  });
  const uniqueFreshActivities = [...freshById.values()];
  const mergedFresh = uniqueFreshActivities.map(freshItem => {
    const existing = existingById.get(String(freshItem._id));
    if (!existing) return freshItem;
    if (deepEqual(existing, freshItem)) return existing;

    const next = { ...freshItem };
    if (!next.social && existing.social) next.social = existing.social;
    if (!next.nutrition_summary && existing.nutrition_summary) next.nutrition_summary = existing.nutrition_summary;
    if (!next.volume_summary && existing.volume_summary) next.volume_summary = existing.volume_summary;
    return next;
  });

  const freshIds = new Set(freshById.keys());
  const preservedTail = preserveBeforeCreatedAt
    ? existingActivities.filter(activity => (
      !freshIds.has(String(activity._id))
      && activity.created_at
      && new Date(activity.created_at) < new Date(preserveBeforeCreatedAt)
    ))
    : [];

  return [...mergedFresh, ...preservedTail]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
};
